import type { Token } from "../types/session";

/**
 * SelectOption interface for dropdown components
 */
export interface SelectOption {
    label: string;
    value: string;
    icon?: string;
    isVerified?: boolean;
}

/**
 * Convert a Token object to SelectOption format for dropdown components
 *
 * @param token - Token from useSessionStore
 * @returns SelectOption with label (symbol), value (address), and icon (logo)
 *
 * @example
 * const option = convertAssetToSelectOption(usdcToken);
 * // { label: "USDC", value: "EPj...xyz", icon: "https://..." }
 */
export function convertAssetToSelectOption(token: Token | undefined): SelectOption {
    if (!token) {
        return {
            label: "",
            value: "",
            icon: undefined
        };
    }

    return {
        label: token.symbol,
        value: token.address,
        icon: token.logo
    };
}

/**
 * Get decimals for a token by address or symbol
 *
 * @param currency - Token address or symbol to lookup
 * @param tokens - Array of tokens from useSessionStore
 * @param isCurrencySymbol - If true, match by symbol; if false, match by address
 * @returns Token decimals, defaults to 9 if not found
 *
 * @example
 * const decimals = getAssetDecimals("EPj...xyz", tokens, false);
 * const decimalsBySymbol = getAssetDecimals("USDC", tokens, true);
 */
export function getAssetDecimals(currency: string, tokens?: Token[], isCurrencySymbol: boolean = false): number {
    if (!tokens || !currency) {
        return 9; // Default SOL decimals
    }

    const token = tokens.find((t) => (isCurrencySymbol ? t.symbol === currency : t.address === currency));

    const decimals = token?.decimals;
    return decimals !== undefined && decimals !== 0 ? decimals : 9;
}
